/**
 * Helpers puros para procesar un archivo GeoJSON arrastrado al dropzone
 * de carga de polígono privado (Fase 4 del handoff parcelas privadas v2).
 *
 * Función pura testeable sin React. El componente UploadParcelaPrivada usa
 * estos helpers + maneja UI/upload state.
 */

export type SimpleGeometry =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

export interface ParseResult {
  ok: true;
  /** Geometría normalizada (Polygon o MultiPolygon WGS84). */
  geom: SimpleGeometry;
  /** Feature/FeatureCollection o Geometry tal como vino del usuario. */
  sourceRaw: unknown;
  /** Warnings que mostrar al user (FeatureCollection con > 1 feature, etc.). */
  warnings: string[];
}

export interface ParseError {
  ok: false;
  error: string;
}

/** Bounds aprox de México: lat ∈ [14, 33], lng ∈ [-118, -86]. */
const MX_LAT_MIN = 14;
const MX_LAT_MAX = 33;
const MX_LNG_MIN = -118;
const MX_LNG_MAX = -86;

/**
 * Extrae la geometría primaria de un objeto GeoJSON crudo. Acepta:
 *   - Feature → toma .geometry
 *   - FeatureCollection → toma .features[0].geometry, warn si > 1
 *   - Geometry directa (Polygon|MultiPolygon)
 * Devuelve ParseError si la estructura no encaja.
 */
export function parseUploadedGeoJSON(raw: unknown): ParseResult | ParseError {
  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, error: 'No es JSON válido.' };
  }
  const obj = raw as { type?: unknown };
  if (typeof obj.type !== 'string') {
    return { ok: false, error: 'Falta el campo "type".' };
  }

  const warnings: string[] = [];
  let geom: unknown;

  switch (obj.type) {
    case 'Feature':
      geom = (obj as { geometry?: unknown }).geometry;
      break;
    case 'FeatureCollection': {
      const features = (obj as { features?: unknown }).features;
      if (!Array.isArray(features) || features.length === 0) {
        return { ok: false, error: 'FeatureCollection vacía.' };
      }
      if (features.length > 1) {
        warnings.push(`El archivo trae ${features.length} features. Solo se carga la primera.`);
      }
      const first = features[0] as { geometry?: unknown };
      geom = first?.geometry;
      break;
    }
    case 'Polygon':
    case 'MultiPolygon':
      geom = obj;
      break;
    default:
      return { ok: false, error: `Tipo no soportado: ${obj.type}. Esperado Feature, FeatureCollection, Polygon o MultiPolygon.` };
  }

  if (typeof geom !== 'object' || geom === null) {
    return { ok: false, error: 'No se encontró geometría.' };
  }

  const g = geom as { type?: unknown; coordinates?: unknown };
  if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') {
    return { ok: false, error: `Geometría no soportada: ${String(g.type)}. Esperado Polygon o MultiPolygon.` };
  }
  if (!Array.isArray(g.coordinates)) {
    return { ok: false, error: 'Coordenadas inválidas.' };
  }

  if (!isWithinMexicoBounds(g as SimpleGeometry)) {
    return { ok: false, error: 'El polígono está fuera del rango razonable de México (lat 14-33, lng -118 a -86).' };
  }

  return {
    ok: true,
    geom: g as SimpleGeometry,
    sourceRaw: raw,
    warnings,
  };
}

/**
 * Verifica que TODOS los puntos del polígono estén dentro del bbox aprox
 * de México. Detección barata para descartar GeoJSONs en otra proyección
 * (EPSG:3857 daría coordenadas en metros, lejísimos de los grados) o de
 * otros países por error.
 */
export function isWithinMexicoBounds(geom: SimpleGeometry): boolean {
  const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;
  for (const poly of polys) {
    for (const ring of poly) {
      for (const pt of ring) {
        if (!Array.isArray(pt) || pt.length < 2) return false;
        const [lng, lat] = pt;
        if (typeof lng !== 'number' || typeof lat !== 'number') return false;
        if (lng < MX_LNG_MIN || lng > MX_LNG_MAX) return false;
        if (lat < MX_LAT_MIN || lat > MX_LAT_MAX) return false;
      }
    }
  }
  return true;
}
